import React, {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    FormControlLabel,
    TextField,
    InputAdornment,
    Typography,
    Box,
    CircularProgress
} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SearchIcon from '@material-ui/icons/Search';
import {makeStyles, fade} from '@material-ui/core/styles';
import {FixedSizeList} from 'react-window';
import {useSnackbar} from 'notistack';
import API_Service, {axiosURIencode} from '../../Services/API_Service';
import {req_cust_chain, req_cust_list} from '../../Helper/Constants';

const scrollbarStyles = (theme) => ({
    '&::-webkit-scrollbar': {width: 8},
    '&::-webkit-scrollbar-track': {backgroundColor: 'transparent'},
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: fade(theme.palette.text.primary, 0.2),
        borderRadius: 4,
        '&:hover': {
            backgroundColor: fade(theme.palette.text.primary, 0.32)
        }
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${fade(theme.palette.text.primary, 0.2)} transparent`
});

const useStyles = makeStyles((theme) => ({
    titleBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    titleSelectAll: {
        margin: 0
    },
    titleSelectAllLabel: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary
    },
    searchField: {
        marginBottom: theme.spacing(1)
    },
    chainRow: {
        backgroundColor: theme.palette.action.hover
    },
    outletRow: {
        paddingLeft: theme.spacing(5)
    },
    chainLabel: {
        fontWeight: 600
    },
    chevron: {
        color: theme.palette.text.secondary
    },
    listWrap: {
        maxHeight: 360,
        overflowY: 'auto',
        marginTop: theme.spacing(1),
        ...scrollbarStyles(theme)
    },
    outletScroll: {
        ...scrollbarStyles(theme)
    },
    footerCount: {
        flex: 1,
        marginLeft: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: '0.75rem'
    }
}));

const OUTLET_ITEM_SIZE = 44;

export default function OutletChainSelector({open, onClose, onConfirm, value = {}, axiosConfig, language}) {
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();

    const [chains, setChains] = useState([]);
    const [loadingChains, setLoadingChains] = useState(false);
    const [search, setSearch] = useState('');
    const [expandedChainId, setExpandedChainId] = useState(null);
    const [outletsByChain, setOutletsByChain] = useState({});
    const [loadingOutletChainId, setLoadingOutletChainId] = useState(null);

    const [appliesToAll, setAppliesToAll] = useState(false);
    const [selectedChainIds, setSelectedChainIds] = useState([]);
    const [selectedOutletIds, setSelectedOutletIds] = useState([]);
    const [outletChainMap, setOutletChainMap] = useState({});

    useEffect(() => {
        if (open) {
            setAppliesToAll(value.appliesToAll === true);
            setSelectedChainIds(Array.isArray(value.chainIds) ? value.chainIds.map(Number) : []);
            setSelectedOutletIds(Array.isArray(value.outletIds) ? value.outletIds.map(Number) : []);
            setOutletChainMap(value.outletChainMap || {});
            setSearch('');
            setExpandedChainId(null);
            loadChains();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const loadChains = () => {
        if (!axiosConfig || Object.keys(axiosConfig).length === 0) {
            return;
        }
        setLoadingChains(true);
        API_Service.get(req_cust_chain, axiosConfig)
            .then(({data}) => {
                const {result, extraData} = data;
                if (result === 1 && Array.isArray(extraData)) {
                    setChains(extraData.filter((c) => `${c.cust_chain_status}` === '1'));
                } else {
                    setChains([]);
                    enqueueSnackbar(language.no_matches_found || 'No chains found', {variant: 'warning'});
                }
            })
            .catch(() => enqueueSnackbar(language.no_internet || 'Network error', {variant: 'error'}))
            .finally(() => setLoadingChains(false));
    };

    const loadOutlets = (chainId) => {
        if (outletsByChain[chainId]) {
            return;
        }
        setLoadingOutletChainId(chainId);
        const params = axiosURIencode({cust_chain_id: chainId});
        API_Service.get(`${req_cust_list}?${params}`, axiosConfig)
            .then(({data}) => {
                const {result, extraData} = data;
                const list = (result === 1 && Array.isArray(extraData))
                    ? extraData.filter((o) => `${o.customer_status}` === '1')
                    : [];
                setOutletsByChain((prev) => ({...prev, [chainId]: list}));
                setOutletChainMap((prev) => {
                    const next = {...prev};
                    list.forEach((o) => {
                        next[`${o.cust_id}`] = chainId;
                    });
                    return next;
                });
            })
            .catch(() => enqueueSnackbar(language.no_internet || 'Network error', {variant: 'error'}))
            .finally(() => setLoadingOutletChainId(null));
    };

    const toggleExpand = (chainId) => {
        const next = expandedChainId === chainId ? null : chainId;
        setExpandedChainId(next);
        if (next !== null) {
            loadOutlets(chainId);
        }
    };

    const toggleAll = () => {
        const next = !appliesToAll;
        setAppliesToAll(next);
        if (next) {
            setSelectedChainIds([]);
            setSelectedOutletIds([]);
        }
    };

    const toggleChain = (chainId) => {
        if (appliesToAll) {
            return;
        }
        setSelectedChainIds((prev) =>
            prev.includes(chainId) ? prev.filter((id) => id !== chainId) : [...prev, chainId]
        );
        const chainOutletIds = (outletsByChain[chainId] || []).map((o) => Number(o.cust_id));
        setSelectedOutletIds((prev) => prev.filter((id) => !chainOutletIds.includes(id)));
    };

    const toggleOutlet = (custId) => {
        if (appliesToAll) {
            return;
        }
        setSelectedOutletIds((prev) =>
            prev.includes(custId) ? prev.filter((id) => id !== custId) : [...prev, custId]
        );
    };

    const handleConfirm = () => {
        onConfirm({
            appliesToAll,
            chainIds: appliesToAll ? [] : selectedChainIds,
            outletIds: appliesToAll ? [] : selectedOutletIds,
            outletChainMap: appliesToAll ? {} : outletChainMap
        });
    };

    const searchTerm = search.trim().toLowerCase();
    const isSearching = searchTerm.length > 0;
    const outletMatches = (o) =>
        `${o.cust_company_name || ''} ${o.cust_code || ''}`.toLowerCase().includes(searchTerm);
    const chainNameMatches = (c) =>
        `${c.cust_chain_name || ''}`.toLowerCase().includes(searchTerm);

    const filteredChains = chains.filter((c) => {
        if (!isSearching) {
            return true;
        }
        if (chainNameMatches(c)) {
            return true;
        }
        const loaded = outletsByChain[Number(c.cust_chain_id)] || [];
        return loaded.some(outletMatches);
    });

    const selectionSummary = () => {
        const c = selectedChainIds.length;
        const o = selectedOutletIds.length;
        if (c === 0 && o === 0) {
            return language.all_outlets || 'All Outlets';
        }
        const parts = [];
        if (c > 0) {
            parts.push(`${c} ${language.unit_chains || 'chains'}`);
        }
        if (o > 0) {
            parts.push(`${o} ${language.unit_outlets || 'outlets'}`);
        }
        return `${parts.join(', ')} ${language.selected || 'selected'}`;
    };

    const renderOutletRow = (chainId, list) => ({index, style}) => {
        const outlet = list[index];
        const custId = Number(outlet.cust_id);
        const chainSelected = selectedChainIds.includes(chainId);
        const checked = appliesToAll || chainSelected || selectedOutletIds.includes(custId);
        return (
            <ListItem key={custId} style={style} dense className={classes.outletRow}>
                <ListItemIcon>
                    <Checkbox
                        edge="start"
                        color="primary"
                        checked={checked}
                        disabled={appliesToAll || chainSelected}
                        onChange={() => toggleOutlet(custId)}
                    />
                </ListItemIcon>
                <ListItemText primary={outlet.cust_company_name} secondary={outlet.cust_code}/>
            </ListItem>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography className={classes.titleBar}>
                <Typography variant="h6">{language.outlet_visibility}</Typography>
                <FormControlLabel
                    className={classes.titleSelectAll}
                    classes={{label: classes.titleSelectAllLabel}}
                    control={
                        <Checkbox
                            color="primary"
                            size="small"
                            checked={appliesToAll}
                            onChange={toggleAll}
                        />
                    }
                    label={language.select_all_outlets}
                    labelPlacement="start"
                />
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    className={classes.searchField}
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder={language.search_outlets}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        )
                    }}
                />

                {loadingChains ? (
                    <Box className={classes.listWrap}>
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} animation="wave" height={44}/>
                        ))}
                    </Box>
                ) : (
                    <List className={classes.listWrap}>
                        {filteredChains.map((chain) => {
                            const chainId = Number(chain.cust_chain_id);
                            const allOutlets = outletsByChain[chainId] || [];
                            const outlets = isSearching ? allOutlets.filter(outletMatches) : allOutlets;
                            const expanded = isSearching ? outlets.length > 0 : expandedChainId === chainId;
                            const isLoadingOutlets = !isSearching && loadingOutletChainId === chainId;
                            const chainOutletIds = allOutlets.map((o) => Number(o.cust_id));
                            const chainLoaded = chainOutletIds.length > 0;
                            const selectedInChainCount = selectedOutletIds.filter((id) => Number(outletChainMap[`${id}`]) === chainId).length;
                            const chainLevelSelected = selectedChainIds.includes(chainId);
                            const allOutletsSelected = chainLoaded && selectedInChainCount === chainOutletIds.length;
                            const chainChecked = appliesToAll || chainLevelSelected || allOutletsSelected;
                            const chainIndeterminate = !chainChecked && selectedInChainCount > 0;
                            return (
                                <React.Fragment key={chainId}>
                                    <ListItem button dense className={classes.chainRow} onClick={() => toggleExpand(chainId)}>
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                color="primary"
                                                checked={chainChecked}
                                                indeterminate={chainIndeterminate}
                                                disabled={appliesToAll}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => toggleChain(chainId)}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            classes={{primary: classes.chainLabel}}
                                            primary={chain.cust_chain_name}
                                        />
                                        {expanded
                                            ? <ExpandMoreIcon className={classes.chevron}/>
                                            : <ChevronRightIcon className={classes.chevron}/>}
                                    </ListItem>
                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                        {isLoadingOutlets ? (
                                            <Box display="flex" justifyContent="center" p={2}>
                                                <CircularProgress size={24}/>
                                            </Box>
                                        ) : outlets.length === 0 ? (
                                            <Box p={2}>
                                                <Typography variant="body2" color="textSecondary">
                                                    {language.no_matches_found}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <FixedSizeList
                                                className={classes.outletScroll}
                                                height={Math.min(outlets.length, 6) * OUTLET_ITEM_SIZE}
                                                width="100%"
                                                itemSize={OUTLET_ITEM_SIZE}
                                                itemCount={outlets.length}
                                            >
                                                {renderOutletRow(chainId, outlets)}
                                            </FixedSizeList>
                                        )}
                                    </Collapse>
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Typography variant="body2" className={classes.footerCount}>
                    {appliesToAll ? language.all_outlets : selectionSummary()}
                </Typography>
                <Button onClick={onClose} color="secondary">{language.cancel}</Button>
                <Button onClick={handleConfirm} color="primary" variant="contained">{language.done}</Button>
            </DialogActions>
        </Dialog>
    );
}
